// Wall-clock ↔ instant conversion for IANA time zones using only Intl (no tz database bundled).

export interface LocalDate {
  y: number;
  m: number; // 1–12
  d: number;
}

export interface Wall extends LocalDate {
  hh: number;
  mm: number;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

export function systemTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function instantToWall(t: number, tz: string): Wall & { ss: number } {
  const parts = formatter(tz).formatToParts(new Date(t));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: get('year'), m: get('month'), d: get('day'), hh: get('hour') % 24, mm: get('minute'), ss: get('second') };
}

/** Offset (local − UTC) in ms at instant `t`. */
export function offsetAt(t: number, tz: string): number {
  const w = instantToWall(t, tz);
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm, w.ss);
  return asUtc - Math.floor(t / 1000) * 1000;
}

/**
 * Convert a local wall time to an instant.
 * - Non-existent times (spring-forward gap) move forward by the gap (02:30 → 03:30).
 * - Ambiguous times (fall-back overlap) resolve to the first occurrence.
 */
export function wallToInstant(w: Wall, tz: string): number {
  const utc = Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm);
  const before = offsetAt(utc - 864e5, tz);
  const after = offsetAt(utc + 864e5, tz);
  const candidates = [...new Set([utc - before, utc - after])].filter((t) => {
    const back = instantToWall(t, tz);
    return back.y === w.y && back.m === w.m && back.d === w.d && back.hh === w.hh && back.mm === w.mm;
  });
  if (candidates.length) return Math.min(...candidates);
  return utc - before;
}

// ---- pure calendar arithmetic (no time zone involved)

export function parseDate(s: string): LocalDate {
  const [y, m, d] = s.split('-').map(Number);
  return { y, m, d };
}

export function formatDate(d: LocalDate): string {
  return `${String(d.y).padStart(4, '0')}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
}

export function parseTime(s: string): { hh: number; mm: number } {
  const [hh, mm] = s.split(':').map(Number);
  return { hh, mm };
}

export function dayNumber(d: LocalDate): number {
  return Math.floor(Date.UTC(d.y, d.m - 1, d.d) / 864e5);
}

export function fromDayNumber(n: number): LocalDate {
  const dt = new Date(n * 864e5);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

export function weekday(d: LocalDate): number {
  return new Date(Date.UTC(d.y, d.m - 1, d.d)).getUTCDay();
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function localDateOf(t: number, tz: string): LocalDate {
  const w = instantToWall(t, tz);
  return { y: w.y, m: w.m, d: w.d };
}

export function todayString(tz: string, now = Date.now()): string {
  return formatDate(localDateOf(now, tz));
}
